import { JSX } from 'react';
import { FLAG_MAP } from '../../../utils/flags';
import {
  GiBroccoli,
  GiLindenLeaf,
  GiMilkCarton,
  GiSouthAmerica,
  GiAfrica,
  GiGreekTemple,
} from 'react-icons/gi';
import { TbBreadOff } from 'react-icons/tb';
import {
  FaPepperHot,
  FaDollarSign,
  FaFish,
  FaDrumstickBite,
  FaLeaf,
  FaBreadSlice,
  FaTint,
  FaAppleAlt,
  FaFire,
  FaMosque,
} from 'react-icons/fa';
import { FaSun } from "react-icons/fa6";
import { LuWheatOff } from 'react-icons/lu';
import { FiClock, FiSmile } from 'react-icons/fi';

export interface TagProps {
  name: string;
  category:
    | 'diet'
    | 'spice'
    | 'cost'
    | 'cuisine'
    | 'nutrition'
    | 'meal'
    | 'occasion'
    | 'season';
  variant?: 'pill' | 'chip';
  size?: 'sm' | 'md';
  isSelected?: boolean;
  onClick?: () => void;
}

const DIET_CONFIG: Record<string, { icon: JSX.Element; bg: string; text: string }> = {
  vegan:        { icon: <GiBroccoli />,    bg: 'bg-green-800',   text: 'text-green-100' },
  vegetarian:   { icon: <GiLindenLeaf />,   bg: 'bg-green-200',   text: 'text-green-800' },
  pescetarian:  { icon: <FaFish />,         bg: 'bg-sky-200',     text: 'text-sky-800' },
  keto_friendly:{ icon: <TbBreadOff />,     bg: 'bg-orange-200',  text: 'text-orange-800' },
  gluten_free:  { icon: <LuWheatOff />,     bg: 'bg-yellow-100',  text: 'text-yellow-800' },
  lactose_free: { icon: <GiMilkCarton />,   bg: 'bg-purple-100',  text: 'text-purple-800' },
};

const NUTRITION_CONFIG: Record<string, { icon: JSX.Element; bg: string; text: string }> = {
  high_protein:{ icon: <FaDrumstickBite />, bg: 'bg-blue-200',   text: 'text-blue-800' },
  high_fiber:  { icon: <FaLeaf />,          bg: 'bg-yellow-200', text: 'text-yellow-800' },
  low_carb:    { icon: <FaBreadSlice />,    bg: 'bg-red-200',    text: 'text-red-800' },
  low_fat:     { icon: <FaTint />,          bg: 'bg-green-200',  text: 'text-green-800' },
  low_sugar:   { icon: <FaAppleAlt />,      bg: 'bg-pink-200',   text: 'text-pink-800' },
  low_calorie: { icon: <FaFire />,          bg: 'bg-purple-200', text: 'text-purple-800' },
};

const REGION_CONFIG: Record<string, { icon: JSX.Element }> = {
  south_american: { icon: <GiSouthAmerica /> },
  african:        { icon: <GiAfrica /> },
  middle_eastern: { icon: <FaMosque /> },
  mediterranean:  { icon: <GiGreekTemple /> },
  balkan:         { icon: <FaSun /> },
};

export default function Tag({
  name,
  category,
  variant = 'pill',
  size = 'sm',
  isSelected = false,
  onClick,
}: TagProps) {
  let display = name.replace(/_/g, ' ');
  let bgClass = 'bg-gray-100';
  let textClass = 'text-gray-800';
  let innerIcon: JSX.Element | null = null;

  switch (category) {
    case 'diet':
      if (DIET_CONFIG[name]) {
        const cfg = DIET_CONFIG[name];
        innerIcon = cfg.icon; bgClass = cfg.bg; textClass = cfg.text;
      }
      break;

    case 'spice':
      if (name === 'none') {
        display = 'Not spicy';
        bgClass = 'bg-red-100'; textClass = 'text-red-800';
      } else {
        const lvlMap = { mild: 1, medium: 2, hot: 3 };
        const lvl = lvlMap[name as keyof typeof lvlMap] || 0;
        innerIcon = (
          <span className="flex">
            {[1, 2, 3].map((i) => (
              <FaPepperHot
                key={i}
                className={i <= lvl ? 'opacity-100' : 'opacity-30'}
              />
            ))}
          </span>
        );
        bgClass = 'bg-red-100'; textClass = 'text-red-800';
      }
      break;

    case 'cost':
      if (name === 'quick') {
        innerIcon = <FiClock />; bgClass = 'bg-indigo-200'; textClass = 'text-indigo-800';
      } else if (name === 'easy') {
        innerIcon = <FiSmile />; bgClass = 'bg-green-200'; textClass = 'text-green-800';
      } else {
        const lvlMap = { cheap: 1, mid_range: 2, expensive: 3 };
        const lvl = lvlMap[name as keyof typeof lvlMap] || 0;
        innerIcon = (
          <span className="flex">
            {[1, 2, 3].map((i) => (
              <FaDollarSign
                key={i}
                className={i <= lvl ? 'opacity-100' : 'opacity-30'}
              />
            ))}
          </span>
        );
        bgClass = 'bg-green-100'; textClass = 'text-green-800';
      }
      break;

    case 'cuisine':
      if (FLAG_MAP[name]) {
        innerIcon = (
          <img
            src={`/flags/4x3/${FLAG_MAP[name]}.svg`}
            alt={display}
            className="w-4 h-3 inline-block mr-1"
          />
        );
      } else if (REGION_CONFIG[name]) {
        innerIcon = (
          <span className="inline-flex items-center mr-1">
            {REGION_CONFIG[name].icon}
          </span>
        );
      }
      bgClass = 'bg-blue-200'; textClass = 'text-blue-800';
      break;

    case 'nutrition':
      if (NUTRITION_CONFIG[name]) {
        const cfg = NUTRITION_CONFIG[name];
        innerIcon = cfg.icon; bgClass = cfg.bg; textClass = cfg.text;
      }
      break;
  }

  if (isSelected) {
    bgClass = 'bg-primary'; textClass = 'text-white';
  }

  // show text for everything except purely icon‐only cost/spice (cheap/mid/expensive)
  const showLabel =
    category === 'spice' ||
    (category === 'cost' && (name === 'quick' || name === 'easy')) ||
    category !== 'cost';

  // wider padding for cuisine tags
  const padding = category === 'cuisine' ? 'px-3 py-0.5' : 'px-2 py-0.5';
  const fontSize = size === 'sm' ? 'text-xs' : 'text-sm';

  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        onClick?.();
      }}
      className={[
        'inline-flex items-center space-x-1 whitespace-nowrap',
        'flex-shrink-0',             // ← prevent ever shrinking in a flex row
        variant === 'pill' ? 'rounded-full' : 'rounded',
        padding, bgClass, textClass, fontSize,
      ].join(' ')}
    >
      {innerIcon}
      {showLabel && (
        <span className="capitalize whitespace-nowrap">{display}</span>
      )}
    </button>
  );
}
